import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from 'styled-components';
import { useAudioPlayerStore } from '@/stores/audioPlayerStore';
import { GradientButton } from '@/components/button/Button';
import { Icon } from '@/styles/Icon';
import { PlayerInfo } from './PlayerInfo';
import { PlayerControls } from './PlayerControls';
import { PlayerVolume } from './PlayerVolume';
import { getButtonState } from './playerUtils';
import { CloseButton as SharedCloseButton } from '@/styles/IconButton';
import { getPlayHistory } from '@/utils/playHistoryUtils';

export const BottomPlayer: React.FC = () => {
  const navigate = useNavigate();
  const {
    currentTrack,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    togglePlay,
    seek,
    setVolume,
    toggleMute,
    closePlayer,
    playNext,
    playPrevious,
  } = useAudioPlayerStore();

  const [hasPrevious, setHasPrevious] = useState(false);
  const [hasNext, setHasNext] = useState(false);

  // 이전곡/다음곡 존재 여부 체크 함수
  const updateNavigationState = () => {
    if (!currentTrack) return;

    const playlist = getPlayHistory();
    const currentIndex = playlist.findIndex(track => track.id === currentTrack.id);

    setHasPrevious(currentIndex > 0);
    setHasNext(currentIndex !== -1 && currentIndex < playlist.length - 1);
  };

  // 이전곡/다음곡 존재 여부 체크
  useEffect(() => {
    updateNavigationState();

    // 세션 스토리지 변경 감지 (같은 탭 내에서)
    const interval = setInterval(updateNavigationState, 500);

    // 다른 탭에서의 storage 변경 감지
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'nowPlayingTracks') {
        updateNavigationState();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [currentTrack]);

  if (!currentTrack) return null;

  const handleBuyClick = () => {
    navigate(`/product/${currentTrack.id}`);
  };

  const buttonState = getButtonState(currentTrack);

  const handlePlaylistClick = () => {
    // 플레이리스트 페이지로 이동
    navigate('/playlist');
  };

  const handlePrevious = () => {
    if (hasPrevious) {
      playPrevious();
    }
  };

  const handleNext = () => {
    if (hasNext) {
      playNext();
    }
  };

  return (
    <PlayerContainer>
      {/* 왼쪽: 트랙 정보 */}
      <PlayerInfo track={currentTrack} onClick={handlePlaylistClick} maxLeftWidth="280px" />

      {/* 중앙: 플레이어 컨트롤 (이전곡, 재생/일시정지, 다음곡, 프로그레스 바) */}
      <CenterSection>
        <PlayerControls
          isPlaying={isPlaying}
          currentTime={currentTime}
          duration={duration}
          onTogglePlay={togglePlay}
          onSeek={seek}
          prevButton={
            <NavButton onClick={handlePrevious} disabled={!hasPrevious}>
              <Icon name="prevPlay" size={40} />
            </NavButton>
          }
          nextButton={
            <NavButton onClick={handleNext} disabled={!hasNext}>
              <Icon name="nextPlay" size={40} />
            </NavButton>
          }
        />
      </CenterSection>

      {/* 오른쪽: 볼륨 & 구매 버튼 */}
      <RightSection>
        <PlayerVolume
          volume={volume}
          isMuted={isMuted}
          onVolumeChange={setVolume}
          onToggleMute={toggleMute}
        />

        {currentTrack.id && buttonState.show && (
          <GradientButton onClick={handleBuyClick} width="112px" height="50px" icon="cartWhite">
            {buttonState.text}
          </GradientButton>
        )}
      </RightSection>

      <CloseButton onClick={closePlayer}>
        <Icon name="close" size={20} />
      </CloseButton>
    </PlayerContainer>
  );
};

// Styled Components
const PlayerContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: ${({ theme }) => theme.playerHeight}px;
  background: ${({ theme }) => theme.colors.bgGray[100]};
  border-top: 1px solid ${({ theme }) => theme.colors.borderGrayPlayer};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 60px;
  z-index: ${({ theme }) => theme.zIndex.player};

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    padding: 0 40px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 0 20px;
    height: ${({ theme }) => theme.playerHeightMobile}px;
  }
`;

const CenterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  max-width: 900px;
  justify-content: center;
`;

const NavButton = styled.button<{ disabled: boolean }>`
  background: transparent;
  border: none;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 0.3 : 0.7)};
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex: 0 0 auto;
`;

const CloseButton = styled(SharedCloseButton)`
  right: 20px;

  &:hover {
    color: ${({ theme }) => theme.colors.fontBlack};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    right: 16px;
    top: 10px;
  }
`;
